"use client";

import { ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-slate-200 rounded-xl bg-white shadow-sm">
        <p className="text-xl font-default mb-6 text-slate-900">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
              Telephone (optional)
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-000-0000" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="area_of_interest" rules={{
      required: "Please select an area of interest"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
              Area of Interest
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-12 w-full px-0 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus:ring-0 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300">
                  <SelectValue placeholder="Select an area" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-lg">
                <SelectItem value="trading_partnership">Trading Partnership</SelectItem>
                <SelectItem value="supply_chain">Supply Chain Inquiry</SelectItem>
                <SelectItem value="bulk_procurement">Bulk Procurement</SelectItem>
                <SelectItem value="general_inquiry">General Inquiry</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="workshop_date" rules={{
      required: "Preferred date is required"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
              Preferred Date
            </FormLabel>
            <FormControl>
              <Input type="date" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="experience_level" rules={{
      required: "Please select your experience level"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
              Business Scale
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-wrap gap-3">
                {[{
            value: "startup",
            label: "Startup"
          }, {
            value: "enterprise",
            label: "Enterprise"
          }, {
            value: "global",
            label: "Global"
          }].map((option, i) => <label key={option.value} data-index={i} className="flex items-center gap-3 px-6 py-3 rounded-lg border border-slate-200 cursor-pointer transition-colors duration-300 has-[:checked]:bg-blue-900 has-[:checked]:text-white has-[:checked]:border-blue-900">
                    <RadioGroupItem value={option.value} className="sr-only" />
                    <span className="text-sm font-default tracking-wide">{option.label}</span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="craft_journey" rules={{
      required: "Please tell us about your requirements"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500">
              Tell us about your requirements
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Share your business goals, procurement needs, or partnership aspirations..." className="px-0 py-3 text-lg font-default bg-transparent border-0 border-b border-slate-200 focus-visible:ring-0 focus-visible:border-blue-900 transition-colors duration-300 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="correspondence" render={({
      field
    }) => <FormItem className="flex items-center gap-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="rounded-md" />
            </FormControl>
            <FormLabel className="text-sm font-default text-slate-700 cursor-pointer leading-relaxed">
              I wish to receive trade updates and newsletters
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-600 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-lg text-base font-default tracking-wide group bg-blue-900 hover:bg-blue-950 text-white">
        {isSubmitting ? "Sending Inquiry..." : <>
            Send Inquiry
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500 text-center pt-2">
        Your inquiry is treated with professional confidentiality
      </p>
    </Form>;
}
export default function Contact() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const formBox = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  const details = [{
    label: "Response time",
    value: "Within 24 hours"
  }, {
    label: "Partnership",
    value: "By application only"
  }, {
    label: "Consultation",
    value: "Complimentary"
  }];
  return <section data-section-id="2875" id="contactform" className="bg-white text-slate-900 relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16">
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-20 select-none text-[18rem] leading-none font-default text-slate-900/[0.03]">
        KTEX
      </span>

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={intro.ref} className={`lg:col-span-5 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.3em] uppercase font-sans text-slate-500 mb-8">
              — Contact
            </p>

            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-10">
              Connect
              <span className="block">
                with <em className="italic font-light text-blue-900">KTEX.</em>
              </span>
            </h1>

            <p className="font-default text-base md:text-[17px] leading-relaxed text-slate-600 max-w-[340px] mb-12">
              Our trade specialists are ready to assist with your procurement needs. 
              Submit your details and we will coordinate a personalized consultation.
            </p>

            <div className="space-y-5 mb-12 max-w-sm">
              {details.map((item, i) => <div key={item.label} data-index={i} className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-200">
                  <span className="text-[11px] tracking-[0.2em] uppercase font-sans text-slate-500 self-center">
                    {item.label}
                  </span>
                  <span className="text-sm font-default text-slate-900">
                    {item.value}
                  </span>
                </div>)}
            </div>

            <div className="overflow-hidden rounded-lg w-full max-w-[280px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/trade_consultation_office.webp" alt="Professional trade consultation office" loading="lazy" decoding="async" className="w-full h-auto object-cover" />
            </div>
          </div>

          <div ref={formBox.ref} className={`lg:col-span-7 transition-all duration-1000 delay-150 ease-out ${formBox.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="contact_form" formId="contact_form" sectionName="contactform" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            area_of_interest: "",
            workshop_date: "",
            experience_level: "",
            craft_journey: "",
            correspondence: false
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            area_of_interest: {
              required: true
            },
            workshop_date: {
              required: true
            },
            experience_level: {
              required: true
            },
            craft_journey: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Inquiry" successMessage="Thank you. Your inquiry has been received and our team will respond within 24 hours." errorMessage="We were unable to send your inquiry. Please try again or contact us directly." className="bg-slate-50 rounded-xl p-8 md:p-12 lg:p-16 border border-slate-100 shadow-sm">
              <ReservationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}